import uniqid from 'uniqid';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { CruiseCard } from '@bwoty-web/ui-kit';
import { useIsGlobetrotter } from '@bwoty-web/ui-kit/hooks';
import { bindActionCreators } from 'redux';

import { getProductsIncluded } from '../../utils/mappers/productsIncluded';
import { standaloneUrlHandler } from '../../utils/url/url';
import { sortedCruiseListSelector } from '../../redux/selectors/cruiseList/sortSelectors';
import { filteredCruisePackagesSelector } from '../../redux/selectors/cruiseList/filterSelectors';
import {
  getProductImpression,
  logProductClick,
} from '../../utils/logUtils/CruiseListLogUtils';

import * as CruiseListActions from '../../redux/actions/cruiseListActions';

const CruiseCardList = ({
  filteredAndSortedCruises,
  labels,
  searchInformation,
}) => {
  const isGlobetrotter = useIsGlobetrotter();
  const showPricePerPerson =
    searchInformation?.numberOfAdults === 2 &&
    searchInformation?.numberOfChildren === 0;

  return filteredAndSortedCruises.map((cruisePackage, idx) => {
    const tags = [{ text: cruisePackage.cruise?.categoryText }];
    const firstUsp = labels.cruiseNumberOfNightsText.replace(
      '{numberOfNights}',
      cruisePackage.cruise?.duration,
    );
    const plusMore =
      cruisePackage.cruise?.itinerary?.filter((stop) => stop.resort !== '')
        ?.length <= 4
        ? ''
        : labels.plusMoreText;
    const itinerarySubTitleText = cruisePackage.cruise?.cruiseItinerarySubTitle
      ? `${cruisePackage.cruise?.cruiseItinerarySubTitle} ${plusMore}`
      : '';
    const flightText = cruisePackage.transport ? labels.flightIncludedText : '';
    const hotelNightsText =
      cruisePackage.numberOfHotelNights > 1
        ? `${cruisePackage.numberOfHotelNights} ${labels.manyHotelNightsText}`
        : labels.oneHotelNightText;
    const uspList = cruisePackage.cruise?.usps || [];

    const cruiseCardContent = {
      bullets: [cruisePackage.cruise?.duration > 0 && firstUsp, ...uspList],
      buttonText: labels?.chooseLabel,
      footerDescription: (
        <>
          {getProductsIncluded(cruisePackage.packageTag, labels)}
          <br />
          {cruisePackage.durAndDep}
        </>
      ),
      image: cruisePackage.cruise?.cruiseListImage,
      ksps: [
        cruisePackage.numberOfHotelNights > 0
          ? `${flightText} + ${hotelNightsText}`
          : flightText,
        cruisePackage.cruise?.mealText,
      ].filter(Boolean),
      price: cruisePackage?.totalPriceRounded,
      pricePerPerson: showPricePerPerson && cruisePackage.pricePerPax,
      rating: cruisePackage.cruise?.cruiseShipRating,
      ratingPrefix: cruisePackage.cruise?.cruiseShipName,
      subtitle: itinerarySubTitleText,
      tags: isGlobetrotter ? [] : tags.map((tag) => tag.text),
      title: cruisePackage.tripRuleName,
      url: standaloneUrlHandler(`/${cruisePackage.cruise?.cruiseDetailsLink}`),
    };
    return (
      <CruiseCard
        bullets={cruiseCardContent.bullets}
        buttonText={cruiseCardContent.buttonText}
        footerDescription={cruiseCardContent.footerDescription}
        image={cruiseCardContent.image}
        key={uniqid('cruise-list-card-')}
        ksps={cruiseCardContent.ksps}
        onClick={() =>
          logProductClick(getProductImpression(cruisePackage, idx + 1))
        }
        price={cruiseCardContent.price}
        pricePerPerson={cruiseCardContent.pricePerPerson}
        rating={cruiseCardContent.rating}
        ratingPrefix={cruiseCardContent.ratingPrefix}
        subtitle={cruiseCardContent.subtitle}
        tags={cruiseCardContent.tags}
        title={cruiseCardContent.title}
        url={cruiseCardContent.url}
      />
    );
  });
};

CruiseCardList.propTypes = {
  filteredAndSortedCruises: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  labels: PropTypes.shape({
    chooseLabel: PropTypes.string,
    cruiseNumberOfNightsText: PropTypes.string,
    flightIncludedText: PropTypes.string.isRequired,
    manyHotelNightsText: PropTypes.string,
    oneHotelNightText: PropTypes.string,
    plusMoreText: PropTypes.string,
  }),
  searchInformation: PropTypes.shape({
    numberOfAdults: PropTypes.number,
    numberOfChildren: PropTypes.number,
  }).isRequired,
};

CruiseCardList.defaultProps = {
  labels: {},
};

const mapActions = (dispatch) => {
  return {
    actions: bindActionCreators({ ...CruiseListActions }, dispatch),
  };
};

const mapStateToProps = (state) => {
  const newState = state.toJS();
  const { isInspiration } = newState.common.siteSettings;

  return {
    filteredAndSortedCruises: isInspiration
      ? filteredCruisePackagesSelector(newState)
      : sortedCruiseListSelector(newState),
    labels: newState.labels,
    searchInformation: newState.cruises.searchInformation,
  };
};

export default connect(mapStateToProps, mapActions)(CruiseCardList);
